import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import f1Service from '../../../components/dashboards/services/f1Service';

/**
 * F1 Driver Standings Widget
 * Terminal-style championship leaderboard
 * Shows current season driver rankings with points and gaps
 */
const F1DriverStandings = ({ season = 2025, compact = false }) => {
  const [standings, setStandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
 
  useEffect(() => {
    const fetchStandings = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await f1Service.getDriverStandings(season);

        if (response && response.response) {
          setStandings(response.response);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching driver standings:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchStandings();
  }, [season]);

  // Get team color for driver
  const getTeamColor = (teamName) => {
    const colors = f1Service.getTeamColors(teamName);
    return colors.primary;
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
        <div className="p-4 border-b border-white/5">
          <div className="h-4 w-48 bg-white/10 rounded animate-pulse" />
        </div>
        <div className="p-4 space-y-2">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="h-3 w-8 bg-white/10 rounded animate-pulse" />
              <div className="h-3 flex-1 bg-white/10 rounded animate-pulse" />
              <div className="h-3 w-16 bg-white/10 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white/[0.02] border border-red-500/20 rounded-sm p-4">
        <p className="text-xs text-red-400 font-mono">Error: {error}</p>
      </div>
    );
  }

  // Empty state
  if (standings.length === 0) {
    return (
      <div className="bg-white/[0.02] border border-white/5 rounded-sm p-8 text-center">
        <p className="text-xs text-gray-600 font-mono uppercase tracking-wider">
          No Standings Data Available
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-sm overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider font-mono">
            Driver Championship
          </h3>
          <span className="text-[10px] text-gray-600 font-mono uppercase tracking-wider">
            {season}
          </span>
        </div>
      </div>

      {/* Standings Table - Terminal Style */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          {/* Table Header */}
          <thead>
            <tr className="border-b border-white/5">
              <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">P</th>
              <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">Driver</th>
              <th className="text-left p-3 text-gray-600 font-bold uppercase tracking-wider">Team</th>
              <th className="text-right p-3 text-gray-600 font-bold uppercase tracking-wider">PTS</th>
              <th className="text-right p-3 text-gray-600 font-bold uppercase tracking-wider">Δ</th>
              {!compact && (
                <th className="text-right p-3 text-gray-600 font-bold uppercase tracking-wider">Wins</th>
              )}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {standings.map((standing, index) => {
              const teamColor = getTeamColor(standing.team?.name);
              const isPodium = standing.position <= 3;
              const isLeader = standing.position === 1;

              return (
                <motion.tr
                  key={standing.driver?.id || index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02, duration: 0.2 }}
                  className={`border-b border-white/5 transition-colors duration-150 ${
                    isLeader ? 'bg-[#E10600]/10' : 'hover:bg-white/[0.02]'
                  }`}
                >
                  {/* Position */}
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {/* Position Number */}
                      <span
                        className={`font-bold tabular-nums ${
                          isPodium ? 'text-white' : 'text-gray-500'
                        }`}
                      >
                        {standing.position}
                      </span>
                      {/* Podium Indicator */}
                      {isPodium && (
                        <div
                          className="w-1 h-4 rounded-full"
                          style={{
                            backgroundColor:
                              standing.position === 1
                                ? '#FFD700'
                                : standing.position === 2
                                ? '#C0C0C0'
                                : '#CD7F32'
                          }}
                        />
                      )}
                    </div>
                  </td>

                  {/* Driver Name */}
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      {/* Team Color Bar */}
                      <div
                        className="w-0.5 h-5 rounded-full"
                        style={{ backgroundColor: teamColor }}
                      />
                      {/* Driver Info */}
                      <div>
                        <div className="text-white font-semibold">
                          {standing.driver?.abbr || standing.driver?.name}
                        </div>
                        {!compact && (
                          <div className="text-[10px] text-gray-600 uppercase tracking-wider">
                            #{standing.driver?.number}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Team */}
                  <td className="p-3">
                    <div
                      className="text-gray-400 truncate max-w-[120px]"
                      style={{ color: `${teamColor}cc` }}
                    >
                      {f1Service.getTeamCode(standing.team?.name)}
                    </div>
                  </td>

                  {/* Points */}
                  <td className="p-3 text-right">
                    <span className="text-white font-bold tabular-nums">
                      {standing.points}
                    </span>
                  </td>

                  {/* Gap to Leader */}
                  <td className="p-3 text-right">
                    {standing.behind === 0 || standing.behind === null ? (
                      <span className="text-gray-700 text-[10px]">—</span>
                    ) : (
                      <span className="text-gray-500 tabular-nums">
                        -{standing.behind}
                      </span>
                    )}
                  </td>

                  {/* Wins (if not compact) */}
                  {!compact && (
                    <td className="p-3 text-right">
                      <span className="text-gray-400 tabular-nums">
                        {standing.wins || 0}
                      </span>
                    </td>
                  )}
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer - Total Drivers */}
      <div className="p-3 border-t border-white/5 bg-white/[0.01]">
        <div className="flex items-center justify-between text-[10px] text-gray-600 font-mono uppercase tracking-wider">
          <span>Total Drivers</span>
          <span className="text-white font-bold">{standings.length}</span>
        </div>
      </div>
    </div>
  );
};

export default F1DriverStandings;
